import merge from 'lodash/merge';
import { memo, useMemo } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import { useSettingsContext } from 'src/components/settings';

// system
import { palette } from './palette';
import { shadows } from './shadows';
import { typography } from './typography';
// options
import RTL from './options/right-to-left';
import { customShadows } from './custom-shadows';
import { componentsOverrides } from './overrides';
import { createPresets } from './options/presets';
import { createContrast } from './options/contrast';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

function ThemeProvider({ children }: Props) {
  const settings = useSettingsContext();

  const presets = useMemo(
    () => createPresets(settings.themeColorPresets),
    [settings.themeColorPresets]
  );

  const contrast = useMemo(
    () => createContrast(settings.themeContrast, settings.themeMode),
    [settings.themeContrast, settings.themeMode]
  );

  const basePalette = useMemo(
    () => palette(settings.themeMode),
    [settings.themeMode]
  );

  const baseShadows = useMemo(
    () => shadows(settings.themeMode),
    [settings.themeMode]
  );

  const baseCustomShadows = useMemo(
    () => customShadows(settings.themeMode),
    [settings.themeMode]
  );

  const memoizedValue = useMemo(
    () => ({
      palette: {
        ...basePalette,
        ...presets.palette,
        ...contrast.palette,
      },
      customShadows: {
        ...baseCustomShadows,
        ...presets.customShadows,
      },
      direction: settings.themeDirection,
      shadows: baseShadows,
      shape: { borderRadius: 8 },
      typography,
    }),
    [
      basePalette,
      baseCustomShadows,
      baseShadows,
      settings.themeDirection,
      presets.palette,
      presets.customShadows,
      contrast.palette,
    ]
  );

  const theme = useMemo(() => {
    const createdTheme = createTheme(memoizedValue as ThemeOptions);
    createdTheme.components = merge(componentsOverrides(createdTheme), contrast.components);
    return createdTheme;
  }, [memoizedValue, contrast.components]);

  return (
    <MuiThemeProvider theme={theme}>
      <RTL themeDirection={settings.themeDirection}>
        <CssBaseline />
        {children}
      </RTL>
    </MuiThemeProvider>
  );
}

export default memo(ThemeProvider);
